import React, { useState } from 'react';
import { MailIcon, LockIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useStory } from '../components/StoryContext';
import { SEOHead } from '../components/SEOHead';
type SignupResponse = {
  access_token?: string;
  error?: string;
};
export const CreateAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setIsLoggedIn
  } = useStory();
  const searchParams = new URLSearchParams(location.search);
  const bookId = searchParams.get('book_id') || '';
  const page = searchParams.get('page') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('https://bot.messagesimproved.com/storybunny_signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          referrer: '',
          book_id: bookId
        })
      });
      const data: SignupResponse = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Signup failed. Please try again.');
      }
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user_email', email);
        setIsLoggedIn(true);
        setShowSuccess(true);
        const redirectTimer = setTimeout(() => {
          if (bookId) {
            // Include page parameter if it exists
            const pageParam = page ? `&page=${page}` : '';
            navigate(`/story?book_id=${bookId}${pageParam}`);
          } else {
            navigate('/garden');
          }
        }, 3000);
        return () => clearTimeout(redirectTimer);
      } else {
        throw new Error('No access token received');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };
  return <>
      <SEOHead title="Create Account - Join Story Bunny" description="Create your free Story Bunny account to save and manage your child's magical AI-generated stories. Start your storytelling adventure today!" canonical="https://storybunny.ai/create-account" data-id="element-1164" />
      <div className="min-h-screen bg-[#FFFBF6] flex flex-col" data-id="element-1165">
        {/* Header */}
        <div className="bg-[#FFFAE4] shadow-sm border-b border-[#E5C1A9] p-4" data-id="element-1166">
          <div className="max-w-7xl mx-auto" data-id="element-1167">
            {/* Removed the h1 element containing "Create Your Account" */}
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-4 mt-[-200px]" data-id="element-1168">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-id="element-1169">
            <div className="text-center mb-8" data-id="element-1170">
              <h2 className="text-3xl font-bold text-[#6B4B3E] mb-4" data-id="element-1171">
                Welcome to Story Bunny!
              </h2>
              <p className="text-[#6B4B3E]/75" data-id="element-1172">
                Create an account to start your magical story adventure
              </p>
            </div>
            {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-id="element-1173">
                {error}
              </div>}
            {showSuccess && <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm flex items-center" data-id="element-1174">
                <CheckCircleIcon size={20} className="mr-2" data-id="element-1175" />
                Account created successfully!{' '}
                {bookId ? 'Redirecting you back to your story...' : 'Redirecting to story garden...'}
              </div>}
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1176">
              <div data-id="element-1177">
                <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1178">
                  Email
                  <span className="text-red-500" data-id="element-1179">*</span>
                </label>
                <div className="relative" data-id="element-1180">
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={isLoading || showSuccess} className="w-full pl-10 pr-4 py-2 border border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088] focus:ring-2 focus:ring-[#E5C1A9]/50 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="you@example.com" data-id="element-1181" />
                  <MailIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#D4A088]" data-id="element-1182" />
                </div>
              </div>
              <div data-id="element-1183">
                <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1184">
                  Password
                  <span className="text-red-500" data-id="element-1185">*</span>
                </label>
                <div className="relative" data-id="element-1186">
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required disabled={isLoading || showSuccess} className="w-full pl-10 pr-4 py-2 border border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088] focus:ring-2 focus:ring-[#E5C1A9]/50 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="••••••••" data-id="element-1187" />
                  <LockIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#D4A088]" data-id="element-1188" />
                </div>
              </div>
              <button type="submit" disabled={isLoading || showSuccess} className="w-full bg-[#D4A088] text-white py-3 rounded-full hover:bg-[#C28B73] transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1189">
                <span className="text-lg" data-id="element-1190">
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </span>
                <ArrowRightIcon size={20} className="ml-2" data-id="element-1191" />
              </button>
            </form>
            <p className="text-center mt-6 text-[#6B4B3E]/60" data-id="element-1192">
              Already have an account?{' '}
              <Link to={`/login${location.search}`} className="text-[#D4A088] hover:underline font-medium" data-id="element-1193">
                Log in here
              </Link>
            </p>
          </div>
        </div>
        {/* Decorative Elements */}
        <div className="fixed bottom-0 left-0 w-24 h-24 p-4" data-id="element-1194">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/e83n1Hq2Bo1NovMS4Gp6fF/ChatGPT_Image_May_17%2C_2025_at_10_47_42_AM.png" alt="Decorative bunny with megaphone" className="w-full h-full object-contain transform -rotate-12" data-id="element-1195" />
        </div>
        <div className="fixed bottom-0 right-0 w-24 h-24 p-4" data-id="element-1196">
          <Link to="/faq" data-id="element-1197">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nDTL5DjtsUQXiGE2CKuuja/ChatGPT_Image_May_17%2C_2025_at_10_41_15_AM.png" alt="Decorative bunny with question mark" className="w-full h-full object-contain transform rotate-12 hover:scale-110 transition-transform cursor-pointer" data-id="element-1198" />
          </Link>
        </div>
      </div>
    </>;
};